import { FilterCatalog } from '../../core/model/filterCatalog';
import type { OpeningHoursDto, RestaurantDto, ReviewDto } from '../dto';

/**
 * Datos de siembra del backend simulado.
 *
 * Están construidos a propósito con **huecos deliberados** (restaurantes sin calificación, sin
 * foto, sin teléfono, sin menú, sin promoción, con campos "null" literales) para que la app se
 * ejecute contra los casos que el análisis exige resolver ocultando componentes en lugar de
 * mostrar campos vacíos. Todos los nombres y contenidos son ficticios.
 */

function hours(days: number[], opens = '13:00', closes = '23:00'): OpeningHoursDto[] {
  return days.map((day) => ({ day_of_week: day, opens_at: opens, closes_at: closes }));
}

function photos(seed: string, count = 3): string[] {
  return Array.from({ length: count }, (_, i) => `https://picsum.photos/seed/${seed}${i + 1}/800/600`);
}

export const restaurants: RestaurantDto[] = [
  {
    id: 'r-001',
    name: 'Casa Malinalli',
    cuisine: 'Mexicana contemporánea',
    price_level: 3,
    rating: 4.7,
    review_count: 412,
    photos: photos('malinalli'),
    address: {
      street: 'Emilio Castelar 121',
      neighborhood: 'Polanco',
      city: 'Ciudad de México',
      latitude: 19.4326,
      longitude: -99.1985,
    },
    distance_meters: 850,
    opening_hours: hours([1, 2, 3, 4, 5, 6, 7]),
    amenities: ['Terraza', 'Bar de mezcales', 'Menú vegetariano', 'Estacionamiento'],
    phone: '5512345678',
    menu_url: 'https://example.invalid/menu/malinalli',
    description: 'Cocina mexicana de temporada con producto de pequeños productores.',
    promotion: { label: '2x1 en entradas', description: 'Lunes a miércoles antes de las 19:00' },
    is_open_now: true,
  },
  {
    id: 'r-002',
    name: 'Trattoria Nove',
    cuisine: 'Italiana',
    price_level: 2,
    rating: 4.3,
    review_count: 189,
    photos: photos('nove', 2),
    address: {
      street: 'Av. Ámsterdam 240',
      neighborhood: 'Condesa',
      city: 'Ciudad de México',
      latitude: 19.411,
      longitude: -99.171,
    },
    distance_meters: 2300,
    opening_hours: hours([2, 3, 4, 5, 6, 7], '12:30', '22:30'),
    amenities: ['Pet friendly', 'Terraza'],
    phone: '5598765432',
    // Sin menu_url: la fila "Ver menú" no debe renderizarse.
    description: 'Pasta fresca hecha en casa todos los días.',
    is_open_now: true,
  },
  {
    id: 'r-003',
    name: 'Sobremesa',
    cuisine: 'De autor',
    price_level: 4,
    rating: 4.9,
    review_count: 96,
    photos: photos('sobremesa', 4),
    address: {
      street: 'Río Pánuco 58',
      neighborhood: 'Cuauhtémoc',
      city: 'Ciudad de México',
      latitude: 19.429,
      longitude: -99.168,
    },
    distance_meters: 3100,
    // Cierra domingo y lunes: sirve para probar TIME_CLOSED / "Cerrado este día".
    opening_hours: hours([2, 3, 4, 5, 6], '19:00', '01:00'),
    amenities: ['Menú degustación', 'Maridaje', 'Reservación obligatoria'],
    phone: '5544332211',
    menu_url: 'https://example.invalid/menu/sobremesa',
    description: 'Menú degustación de 8 tiempos que cambia cada estación.',
    is_open_now: false,
  },
  {
    id: 'r-004',
    name: 'Fonda La Guadalupana',
    cuisine: 'Mexicana tradicional',
    price_level: 1,
    // Sin rating ni reseñas: el bloque de calificación completo debe ocultarse.
    rating: null,
    review_count: 0,
    photos: [], // Sin fotos: se compone el placeholder, no un hueco gris.
    address: {
      street: 'Calle 5 de Mayo 12',
      neighborhood: 'Centro',
      city: 'Ciudad de México',
      latitude: 19.434,
      longitude: -99.137,
    },
    distance_meters: 5400,
    opening_hours: hours([1, 2, 3, 4, 5, 6], '08:00', '18:00'),
    amenities: [], // Sección "Servicios" oculta por completo.
    phone: null, // Fila "Llamar" ausente, no deshabilitada.
    menu_url: null,
    description: null, // Sección "Acerca de" oculta.
    is_open_now: null, // Desconocido ≠ cerrado: no se pinta el chip.
  },
  {
    id: 'r-005',
    name: 'Barra Kanpai',
    cuisine: 'Japonesa',
    price_level: 3,
    rating: 4.5,
    review_count: 271,
    photos: photos('kanpai', 3),
    address: {
      street: 'Av. Nuevo León 88',
      neighborhood: 'Hipódromo',
      city: 'Ciudad de México',
      latitude: 19.409,
      longitude: -99.174,
    },
    distance_meters: 2650,
    opening_hours: hours([2, 3, 4, 5, 6, 7], '13:30', '23:30'),
    amenities: ['Barra omakase', 'Sake bar'],
    phone: '5511223344',
    menu_url: 'https://example.invalid/menu/kanpai',
    description: 'Doce lugares en barra y omakase de mercado.',
    // Este restaurante nunca tendrá slots: sirve para el estado "Sin disponibilidad".
    is_open_now: true,
  },
  {
    id: 'r-006',
    name: 'Verde Limón',
    cuisine: 'Vegetariana',
    price_level: 2,
    rating: 4.1,
    review_count: 58,
    photos: photos('verde', 2),
    address: {
      street: 'Colima 158',
      neighborhood: 'Roma Norte',
      city: 'Ciudad de México',
      latitude: 19.418,
      longitude: -99.162,
    },
    distance_meters: 1900,
    opening_hours: hours([1, 2, 3, 4, 5, 6, 7], '09:00', '21:00'),
    amenities: ['Opciones veganas', 'Sin gluten', 'Terraza'],
    // El backend manda basura literal: el mapper debe convertirla en null.
    phone: 'null',
    menu_url: '  ',
    description: 'N/A',
    promotion: { label: '', description: 'undefined' },
    is_open_now: true,
  },
  {
    id: 'r-007',
    name: 'El Farolito del Puerto',
    cuisine: 'Mariscos',
    price_level: 2,
    rating: 4.4,
    review_count: 333,
    photos: photos('farolito', 3),
    address: {
      street: 'Av. Revolución 1420',
      neighborhood: 'San Ángel',
      city: 'Ciudad de México',
      latitude: 19.346,
      longitude: -99.19,
    },
    distance_meters: 8200,
    opening_hours: hours([1, 2, 3, 4, 5, 6, 7], '11:00', '20:00'),
    amenities: ['Familiar', 'Estacionamiento'],
    phone: '5566778899',
    description: 'Pescados y mariscos del Pacífico, receta veracruzana.',
    promotion: { label: 'Martes de tostadas', description: null },
    is_open_now: true,
  },
  {
    id: 'r-008',
    name: 'Panadería Norte',
    cuisine: 'Cafetería',
    price_level: 1,
    rating: 4.6,
    review_count: 144,
    photos: photos('norte', 2),
    address: {
      street: 'Av. Horacio 210',
      neighborhood: 'Polanco',
      city: 'Ciudad de México',
      latitude: 19.436,
      longitude: -99.193,
    },
    distance_meters: 1050,
    opening_hours: hours([1, 2, 3, 4, 5, 6, 7], '07:00', '20:00'),
    amenities: ['Wi-Fi', 'Para llevar'],
    phone: '5500112233',
    description: 'Masa madre y café de Chiapas.',
    is_open_now: true,
  },
];

/** Restaurantes que el simulador destaca en cada carrusel del Inicio. */
export const recommendedIds = ['r-001', 'r-003', 'r-005', 'r-002'];
export const nearbyIds = ['r-008', 'r-001', 'r-006', 'r-002'];
export const featuredIds = ['r-003', 'r-007', 'r-005'];

/** Nunca devuelve slots, para poder recorrer el flujo "sin disponibilidad". */
export const NO_AVAILABILITY_ID = 'r-005';

/** El primer intento de reservar aquí responde 409 (la mesa se ocupó). */
export const SLOT_TAKEN_ID = 'r-007';

export const reviews: Record<string, ReviewDto[]> = {
  'r-001': [
    {
      id: 'rv-1',
      author_name: 'Ana L.',
      rating: 5.0,
      comment: 'El mole de la casa vale el viaje.',
      date: '2026-08-12',
      food_rating: 5.0,
      service_rating: 5.0,
      ambience_rating: 4.5,
    },
    {
      id: 'rv-2',
      author_name: 'Jorge M.',
      rating: 4.0,
      comment: 'Muy buen servicio, un poco ruidoso.',
      date: '2026-07-30',
      food_rating: 4.5,
      service_rating: 4.5,
      ambience_rating: 3.0,
    },
    // Reseña sin comentario: la tarjeta debe mostrar solo la calificación.
    { id: 'rv-3', author_name: 'Paty R.', rating: 4.5, comment: null, date: '2026-07-02' },
  ],
  'r-002': [
    {
      id: 'rv-4',
      author_name: 'Diego S.',
      rating: 4.0,
      comment: 'La cacio e pepe está muy lograda.',
      date: '2026-08-01',
    },
  ],
  'r-003': [
    {
      id: 'rv-5',
      author_name: 'Fernanda O.',
      rating: 5.0,
      comment: 'Una experiencia completa. Reserven con tiempo.',
      date: '2026-08-20',
      food_rating: 5.0,
      service_rating: 5.0,
      ambience_rating: 5.0,
    },
  ],
};

export const cuisines = FilterCatalog.cuisines;
export const amenities = FilterCatalog.amenities;
export const locations = FilterCatalog.locations;
